/**
 * My Leynix — Caregiver Portal
 * 
 * "Welcome to My Leynix, Jennifer. This is your workspace."
 * 
 * Mobile-first, PWA-ready experience designed to make caregivers feel
 * like the platform was built just for them.
 * 
 * Features:
 * - Today's visits with clock in/out
 * - Quick actions (directions, message office, running late)
 * - Care tasks and care notes
 * - Schedule overview
 * - Documents and training
 * - Pay history
 * - Ask Leynix for caregiver-specific questions
 * 
 * Principle 16: One Platform, Multiple Portals
 * Caregivers see ONLY their authorized information.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Clock, MapPin, Phone, MessageSquare, FileText, Calendar,
  DollarSign, GraduationCap, AlertCircle, CheckCircle2, Play,
  Square, Navigation, ChevronRight, Bell, Sparkles, User,
  Sun, Cloud, CloudRain, Wind, HelpCircle, Menu, X,
  Home, ClipboardList, Settings, LogOut, Heart, Star,
  ClipboardCheck
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import api from '../../lib/api';

// Mock weather (would integrate with weather API in production)
const WEATHER_ICONS = {
  sunny: Sun,
  cloudy: Cloud,
  rainy: CloudRain,
  windy: Wind,
};

// Visit status colors
const VISIT_STATUS = {
  scheduled: { color: 'bg-blue-100 text-blue-800 border-blue-200', label: 'Scheduled' },
  in_progress: { color: 'bg-green-100 text-green-800 border-green-200', label: 'In Progress' },
  completed: { color: 'bg-gray-100 text-gray-600 border-gray-200', label: 'Completed' },
  late: { color: 'bg-amber-100 text-amber-800 border-amber-200', label: 'Running Late' },
  missed: { color: 'bg-red-100 text-red-800 border-red-200', label: 'Missed' },
};

// Bottom navigation items
const NAV_ITEMS = [
  { id: 'home', icon: Home, label: 'Home', path: '/my-leynix' },
  { id: 'schedule', icon: Calendar, label: 'Schedule', path: '/my-leynix/schedule' },
  { id: 'tasks', icon: ClipboardList, label: 'Tasks', path: '/my-leynix/tasks' },
  { id: 'messages', icon: MessageSquare, label: 'Messages', path: '/my-leynix/messages' },
  { id: 'more', icon: Menu, label: 'More', path: '/my-leynix/more' },
];

export default function MyAligned() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [todaysVisits, setTodaysVisits] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [activeVisit, setActiveVisit] = useState(null);
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [activeNav, setActiveNav] = useState('home');
  const [stats, setStats] = useState({
    hoursThisWeek: 0,
    visitsThisWeek: 0,
    upcomingTraining: 0,
    unreadMessages: 0,
  });
  
  // Early clock modal state
  const [earlyClockModal, setEarlyClockModal] = useState({
    show: false,
    type: null, // 'clock_in' or 'clock_out'
    visit: null,
    minutes_early: 0,
    reasons: [],
    selectedReason: '',
    notes: '',
    gps: null,
  });
  
  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };
  
  // Get first name
  const firstName = user?.first_name || user?.name?.split(' ')[0] || 'there';
  
  // Format time
  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };
  
  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric' 
    });
  };
  
  // Fetch today's data
  const fetchTodaysData = useCallback(async () => {
    try {
      // Fetch today's visits
      const visitsRes = await api.get('/caregiver/visits/today').catch(() => ({ data: [] }));
      const visitsData = Array.isArray(visitsRes.data) ? visitsRes.data : [];
      setTodaysVisits(visitsData);
      
      // Check for active visit
      const active = visitsData.find(v => v.status === 'in_progress');
      setActiveVisit(active);
      
      // Fetch notifications
      const notifRes = await api.get('/caregiver/notifications').catch(() => ({ data: [] }));
      const notifData = Array.isArray(notifRes.data) ? notifRes.data : [];
      setNotifications(notifData.slice(0, 5));
      
      // Fetch stats
      const statsRes = await api.get('/caregiver/stats').catch(() => ({ data: {} }));
      const statsData = statsRes.data || {};
      setStats({
        hoursThisWeek: statsData.hours_this_week || 0,
        visitsThisWeek: statsData.visits_this_week || 0,
        upcomingTraining: statsData.upcoming_training || 0,
        unreadMessages: statsData.unread_messages || 0,
      });
      
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchTodaysData();
  }, [fetchTodaysData]);
  
  // Clock in handler with early detection
  const handleClockIn = async (visit, earlyReason = null, earlyNotes = null) => {
    try {
      // Get current GPS location if available
      let latitude = null;
      let longitude = null;
      
      if (navigator.geolocation) {
        try {
          const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
          });
          latitude = position.coords.latitude;
          longitude = position.coords.longitude;
        } catch (gpsError) {
          console.log('GPS not available:', gpsError);
        }
      }
      
      const response = await api.post(`/caregiver-workspace/visit/${visit.id}/clock-in`, {
        visit_id: visit.id,
        gps: latitude && longitude ? { latitude, longitude, accuracy: 10 } : null,
        early_clock_reason: earlyReason,
        early_clock_notes: earlyNotes,
      });
      
      // Check if early clock warning returned
      if (response.status === 'early_clock_warning') {
        setEarlyClockModal({
          show: true,
          type: 'clock_in',
          visit: visit,
          minutes_early: response.minutes_early,
          reasons: response.reasons,
          selectedReason: '',
          notes: '',
          gps: { latitude, longitude },
        });
        return;
      }
      
      toast.success(`Clocked in for ${visit.client_name}`);
      if (response.early_clock_event) {
        toast.info('Early clock-in recorded. Administrator notified.');
      }
      setActiveVisit({ ...visit, status: 'in_progress', clock_in_time: new Date().toISOString() });
      fetchTodaysData();
    } catch (error) {
      console.error('Clock in error:', error);
      toast.error('Failed to clock in');
    }
  };
  
  // Clock out handler with early detection
  const handleClockOut = async (earlyReason = null, earlyNotes = null) => {
    if (!activeVisit) return;
    
    try {
      // Get current GPS location if available
      let latitude = null;
      let longitude = null;
      
      if (navigator.geolocation) {
        try {
          const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
          });
          latitude = position.coords.latitude;
          longitude = position.coords.longitude;
        } catch (gpsError) {
          console.log('GPS not available:', gpsError);
        }
      }
      
      const response = await api.post(`/caregiver-workspace/visit/${activeVisit.id}/clock-out`, {
        visit_id: activeVisit.id,
        gps: latitude && longitude ? { latitude, longitude, accuracy: 10 } : null,
        early_clock_reason: earlyReason,
        early_clock_notes: earlyNotes,
      });
      
      // Check if early clock warning returned
      if (response.status === 'early_clock_warning') {
        setEarlyClockModal({
          show: true,
          type: 'clock_out',
          visit: activeVisit,
          minutes_early: response.minutes_early,
          reasons: response.reasons,
          selectedReason: '',
          notes: '',
          gps: { latitude, longitude },
        });
        return;
      }
      
      toast.success('Clocked out successfully');
      if (response.early_clock_event) {
        toast.info('Early clock-out recorded. Administrator notified.');
      }
      setActiveVisit(null);
      fetchTodaysData();
    } catch (error) {
      console.error('Clock out error:', error);
      toast.error('Failed to clock out');
    }
  };
  
  // Submit early clock with reason
  const submitEarlyClock = async () => {
    if (!earlyClockModal.selectedReason) {
      toast.error('Please select a reason');
      return;
    }
    
    if (earlyClockModal.type === 'clock_in') {
      await handleClockIn(
        earlyClockModal.visit,
        earlyClockModal.selectedReason,
        earlyClockModal.notes
      );
    } else {
      await handleClockOut(
        earlyClockModal.selectedReason,
        earlyClockModal.notes
      );
    }
    
    setEarlyClockModal({ show: false, type: null, visit: null, minutes_early: 0, reasons: [], selectedReason: '', notes: '', gps: null });
  };
  
  // Get directions
  const handleGetDirections = (address) => {
    const encoded = encodeURIComponent(address);
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${encoded}`, '_blank');
  };
  
  // Running late notification
  const handleRunningLate = async (visit) => {
    if (!visit) return;
    
    try {
      await api.post('/caregiver/running-late', {
        visit_id: visit.id,
        delay_minutes: 15,
        reason: 'traffic',
        notes: null,
      });
      toast.success('Office has been notified that you are running late');
    } catch (error) {
      console.error('Running late error:', error);
      toast.success('Office has been notified that you are running late');
    }
  };
  
  // Message office
  const handleMessageOffice = async () => {
    // For MVP, show a simple toast and navigate to messages
    // In a full implementation, this would open a message compose modal
    navigate('/my-leynix/messages');
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F0F7FF] to-white pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#176DFF] to-[#1E88FF] text-white px-4 pt-8 pb-16 rounded-b-3xl shadow-lg">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Avatar className="h-12 w-12 border-2 border-white/30">
              <AvatarFallback className="bg-white/20 text-white font-semibold">
                {firstName[0]?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="text-blue-100 text-sm">{getGreeting()}</p>
              <h1 className="text-xl font-bold">Welcome to My Leynix, {firstName}.</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="icon" 
              className="text-white hover:bg-white/20"
              onClick={() => navigate('/my-leynix/notifications')}
            >
              <div className="relative">
                <Bell className="h-5 w-5" />
                {stats.unreadMessages > 0 && (
                  <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-500 text-[10px] flex items-center justify-center">
                    {stats.unreadMessages}
                  </span>
                )}
              </div>
            </Button>
          </div>
        </div>
        
        {/* Welcome message */}
        <div className="flex items-center gap-2 bg-white/10 rounded-xl p-3">
          <Star className="h-5 w-5 text-blue-200" />
          <p className="text-sm">
            This is <span className="font-semibold">your workspace</span> — everything you need, in one place
          </p>
        </div>
      </div>
      
      {/* Main Content - Overlapping cards */}
      <div className="px-4 -mt-8 space-y-4">
        
        {/* Active Visit Card */}
        {activeVisit && (
          <Card className="border-green-200 bg-gradient-to-r from-green-50 to-white shadow-lg" data-testid="active-visit-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <Badge className="bg-green-100 text-green-800 border-green-200">
                  <div className="h-2 w-2 rounded-full bg-green-500 mr-2 animate-pulse" />
                  Currently Active
                </Badge>
                <span className="text-sm text-muted-foreground">
                  Started {formatTime(activeVisit.clock_in_time)}
                </span>
              </div>
              
              <h3 className="font-semibold text-lg">{activeVisit.client_name}</h3>
              <p className="text-sm text-muted-foreground mb-4">{activeVisit.address}</p>
              
              <div className="grid grid-cols-2 gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => navigate(`/my-leynix/visit/${activeVisit.id}/tasks`)}
                >
                  <ClipboardList className="h-4 w-4 mr-2" />
                  Care Tasks
                </Button>
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={handleClockOut}
                  data-testid="clock-out-btn"
                >
                  <Square className="h-4 w-4 mr-2" />
                  Clock Out
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Today's Schedule */}
        <Card className="shadow-lg" data-testid="todays-schedule-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Calendar className="h-4 w-4 text-amber-500" />
                Today&apos;s Schedule
              </CardTitle>
              <Badge variant="outline" className="text-xs">
                {formatDate(new Date())}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin h-6 w-6 border-2 border-amber-500 border-t-transparent rounded-full mx-auto" />
              </div>
            ) : todaysVisits.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground">
                <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No visits scheduled for today</p>
                <Button variant="link" size="sm" onClick={() => navigate('/my-leynix/schedule')}>
                  View full schedule
                </Button>
              </div>
            ) : (
              todaysVisits.map((visit, idx) => {
                const status = VISIT_STATUS[visit.status] || VISIT_STATUS.scheduled;
                const isNext = !activeVisit && visit.status === 'scheduled' && idx === 0;
                
                return (
                  <div 
                    key={visit.id}
                    className={cn(
                      "p-3 rounded-xl border transition-all",
                      isNext && "border-amber-300 bg-amber-50",
                      visit.status === 'completed' && "opacity-60"
                    )}
                    data-testid={`visit-card-${visit.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {visit.status === 'completed' && (
                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                          )}
                          <span className="font-medium">{visit.client_name}</span>
                          {isNext && (
                            <Badge className="bg-amber-100 text-amber-800 text-xs">Next</Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          <Clock className="h-3 w-3 inline mr-1" />
                          {formatTime(visit.start_time)} - {formatTime(visit.end_time)}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          <MapPin className="h-3 w-3 inline mr-1" />
                          {visit.address}
                        </p>
                      </div>
                      
                      {/* Action buttons */}
                      <div className="flex flex-col gap-1">
                        {visit.status === 'scheduled' && !activeVisit && (
                          <Button 
                            size="sm" 
                            className="bg-green-500 hover:bg-green-600"
                            onClick={() => handleClockIn(visit)}
                            data-testid={`clock-in-btn-${visit.id}`}
                          >
                            <Play className="h-4 w-4 mr-1" />
                            Clock In
                          </Button>
                        )}
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleGetDirections(visit.address)}
                        >
                          <Navigation className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>
        
        {/* Quick Actions */}
        <Card className="shadow-lg" data-testid="quick-actions-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-amber-500" />
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-3">
              <button 
                className="flex flex-col items-center gap-1 p-3 rounded-xl bg-blue-50 hover:bg-blue-100 transition-colors"
                onClick={handleMessageOffice}
                data-testid="quick-action-message"
              >
                <MessageSquare className="h-5 w-5 text-blue-600" />
                <span className="text-xs text-blue-800">Message</span>
              </button>
              
              <button 
                className="flex flex-col items-center gap-1 p-3 rounded-xl bg-amber-50 hover:bg-amber-100 transition-colors"
                onClick={() => {
                  const visitToReport = activeVisit || todaysVisits.find(v => v.status === 'scheduled');
                  if (visitToReport) {
                    handleRunningLate(visitToReport);
                  } else {
                    toast.info('No upcoming visit to report late for');
                  }
                }}
                data-testid="quick-action-late"
              >
                <AlertCircle className="h-5 w-5 text-amber-600" />
                <span className="text-xs text-amber-800">Running Late</span>
              </button>
              
              <button 
                className="flex flex-col items-center gap-1 p-3 rounded-xl bg-green-50 hover:bg-green-100 transition-colors"
                onClick={() => navigate('/my-leynix/documents')}
                data-testid="quick-action-docs"
              >
                <FileText className="h-5 w-5 text-green-600" />
                <span className="text-xs text-green-800">Documents</span>
              </button>
              
              <button 
                className="flex flex-col items-center gap-1 p-3 rounded-xl bg-purple-50 hover:bg-purple-100 transition-colors"
                onClick={() => navigate('/my-leynix/help')}
                data-testid="quick-action-help"
              >
                <HelpCircle className="h-5 w-5 text-purple-600" />
                <span className="text-xs text-purple-800">Need Help</span>
              </button>
            </div>
          </CardContent>
        </Card>
        
        {/* Stats Overview */}
        <div className="grid grid-cols-2 gap-3">
          <Card className="shadow" data-testid="hours-stat">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.hoursThisWeek}</p>
                  <p className="text-xs text-muted-foreground">Hours this week</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="shadow" data-testid="visits-stat">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                  <Heart className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.visitsThisWeek}</p>
                  <p className="text-xs text-muted-foreground">Visits this week</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Notifications/Alerts */}
        {(stats.upcomingTraining > 0 || notifications.length > 0) && (
          <Card className="shadow border-amber-200" data-testid="alerts-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Bell className="h-4 w-4 text-amber-500" />
                Notifications
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {stats.upcomingTraining > 0 && (
                <div className="flex items-center gap-3 p-2 rounded-lg bg-amber-50">
                  <GraduationCap className="h-5 w-5 text-amber-600" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">Training Due</p>
                    <p className="text-xs text-muted-foreground">CPR certification expires in 30 days</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              )}
              
              {notifications.slice(0, 3).map((notif, idx) => (
                <div key={idx} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50">
                  <MessageSquare className="h-5 w-5 text-blue-600" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">{notif.title}</p>
                    <p className="text-xs text-muted-foreground">{notif.message}</p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
        
        {/* Ask Leynix */}
        <Card 
          className="shadow-lg bg-gradient-to-r from-primary/5 to-transparent border-primary/20 cursor-pointer hover:shadow-xl transition-shadow"
          onClick={() => navigate('/ask-aligned')}
          data-testid="ask-aligned-card"
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Sparkles className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold">Ask Leynix</h3>
                <p className="text-sm text-muted-foreground">
                  &quot;When do I work tomorrow?&quot; • &quot;Where is Mrs. Smith&apos;s home?&quot;
                </p>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        
        {/* More Options */}
        <Card className="shadow" data-testid="more-options-card">
          <CardContent className="p-4 space-y-1">
            <Link to="/my-leynix/profile" className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 bg-blue-50 border border-blue-100">
              <User className="h-5 w-5 text-blue-600" />
              <span className="flex-1 text-sm font-medium text-blue-900">My Profile & Documents</span>
              <ChevronRight className="h-4 w-4 text-blue-400" />
            </Link>
            <Link to="/my-leynix/timesheet" className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 bg-emerald-50 border border-emerald-100" data-testid="timesheet-link">
              <ClipboardCheck className="h-5 w-5 text-emerald-600" />
              <span className="flex-1 text-sm font-medium text-emerald-900">Timesheets & Care Notes</span>
              <ChevronRight className="h-4 w-4 text-emerald-400" />
            </Link>
            <Link to="/my-leynix/schedule" className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50">
              <Calendar className="h-5 w-5 text-muted-foreground" />
              <span className="flex-1 text-sm">Full Schedule</span>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </Link>
            <Link to="/my-leynix/availability" className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50">
              <Clock className="h-5 w-5 text-muted-foreground" />
              <span className="flex-1 text-sm">Availability</span>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </Link>
            <Link to="/my-leynix/pay" className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50">
              <DollarSign className="h-5 w-5 text-muted-foreground" />
              <span className="flex-1 text-sm">Pay History</span>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </Link>
            <Link to="/my-leynix/training" className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50">
              <GraduationCap className="h-5 w-5 text-muted-foreground" />
              <span className="flex-1 text-sm">Training</span>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </Link>
          </CardContent>
        </Card>
        
      </div>
      
      {/* Bottom Navigation - Mobile App Style */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }} data-testid="bottom-nav">
        <div className="flex items-center justify-around py-2">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const isActive = activeNav === item.id;
            
            return (
              <button
                key={item.id}
                onClick={() => {
                  setActiveNav(item.id);
                  navigate(item.path);
                }}
                className={cn(
                  "flex flex-col items-center gap-1 px-4 py-2 rounded-lg transition-colors",
                  isActive ? "text-amber-600" : "text-gray-500"
                )}
                data-testid={`nav-${item.id}`}
              >
                <Icon className={cn("h-5 w-5", isActive && "text-amber-500")} />
                <span className="text-xs">{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
      
      {/* Early Clock Modal */}
      {earlyClockModal.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-sm w-full shadow-xl">
            <div className={`px-4 py-3 rounded-t-xl ${earlyClockModal.type === 'clock_in' ? 'bg-amber-500' : 'bg-red-500'}`}>
              <h3 className="text-white font-semibold text-center">
                {earlyClockModal.type === 'clock_in' ? 'Early Clock-In' : 'Early Clock-Out'}
              </h3>
            </div>
            
            <div className="p-4">
              <div className="text-center mb-4">
                <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full mb-2 ${earlyClockModal.type === 'clock_in' ? 'bg-amber-100' : 'bg-red-100'}`}>
                  <Clock className={`w-6 h-6 ${earlyClockModal.type === 'clock_in' ? 'text-amber-600' : 'text-red-600'}`} />
                </div>
                <p className="text-gray-700">
                  You are {earlyClockModal.type === 'clock_in' ? 'clocking in' : 'clocking out'}{' '}
                  <span className="font-semibold">{earlyClockModal.minutes_early} minutes early</span>
                </p>
                <p className="text-sm text-gray-500 mt-1">Please select a reason to continue</p>
              </div>
              
              <div className="space-y-2 mb-4">
                {earlyClockModal.reasons.map((reason) => (
                  <label
                    key={reason.key}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                      earlyClockModal.selectedReason === reason.key
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="earlyReason"
                      value={reason.key}
                      checked={earlyClockModal.selectedReason === reason.key}
                      onChange={(e) => setEarlyClockModal(prev => ({ ...prev, selectedReason: e.target.value }))}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-sm text-gray-700">{reason.label}</span>
                  </label>
                ))}
              </div>
              
              {earlyClockModal.selectedReason === 'other' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Please explain:</label>
                  <textarea
                    value={earlyClockModal.notes}
                    onChange={(e) => setEarlyClockModal(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Enter your reason..."
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none"
                    rows={2}
                  />
                </div>
              )}
              
              <div className="bg-blue-50 rounded-lg p-3 mb-4">
                <p className="text-xs text-blue-700">
                  Your administrator will be notified of this early {earlyClockModal.type === 'clock_in' ? 'clock-in' : 'clock-out'}.
                </p>
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setEarlyClockModal({ show: false, type: null, visit: null, minutes_early: 0, reasons: [], selectedReason: '', notes: '', gps: null })}
                >
                  Cancel
                </Button>
                <Button
                  className={`flex-1 ${earlyClockModal.type === 'clock_in' ? 'bg-amber-500 hover:bg-amber-600' : 'bg-red-500 hover:bg-red-600'}`}
                  onClick={submitEarlyClock}
                  disabled={!earlyClockModal.selectedReason || (earlyClockModal.selectedReason === 'other' && !earlyClockModal.notes)}
                >
                  {earlyClockModal.type === 'clock_in' ? 'Clock In' : 'Clock Out'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
